import { formatTime } from 'central-utils/dateUtils';
import { useMemo } from 'react';

// Define the types more clearly and locally if they aren't globally available
interface OrderItem {
  topMostCategory: 'EAT' | 'DRINK' | string;
  status: string; // e.g., 'NEW', 'CONFIRMED', 'DELIVERED'
}

interface Order {
  orderItems: OrderItem[];
  confirmedAt: string; // ISO 8601 string
}

interface UseLatestTimesReturn {
  latestFoodTime: string | null;
  latestDrinkTime: string | null;
}

/**
 * Custom hook to find the formatted confirmation times of the latest 'EAT' and 'DRINK' orders
 * that are not in 'NEW' status, looping from the most recent orders backward.
 *
 * @param orders An array of Order objects.
 * @returns An object containing the latest formatted times for food and drinks.
 */
export function useLatestOrders(orders: Order[]): UseLatestTimesReturn {
  const { latestFoodTime, latestDrinkTime } = useMemo(() => {
    // Return early for empty or invalid input array
    if (!Array.isArray(orders) || orders.length === 0) {
      return { latestFoodTime: null, latestDrinkTime: null };
    }

    let foodTime: string | null = null;
    let drinkTime: string | null = null;

    // Use a Set to track remaining categories we need to find, for efficient stopping criteria
    const neededCategories = new Set(['EAT', 'DRINK']);

    // Iterate backwards from the most recent order efficiently
    for (let i = orders.length - 1; i >= 0; i--) {
      const order = orders[i];
      // Skip orders that are empty or invalid
      if (!order?.orderItems?.length) continue;

      const orderConfirmedTime = formatTime(order.confirmedAt);

      // Iterate over items in the current order to find relevant categories
      for (const item of order.orderItems) {
        // Only process categories we still need and meet the status criteria
        if (neededCategories.has(item.topMostCategory) && item.status !== 'NEW') {
          if (item.topMostCategory === 'EAT' && foodTime === null) {
            foodTime = orderConfirmedTime;
            neededCategories.delete('EAT');
          } else if (item.topMostCategory === 'DRINK' && drinkTime === null) {
            drinkTime = orderConfirmedTime;
            neededCategories.delete('DRINK');
          }

          // Optimization: If both are found, break from the inner item loop
          if (neededCategories.size === 0) {
            break;
          }
        }
      }

      // Optimization: If both foodTime and drinkTime are set, break from the outer order loop
      if (neededCategories.size === 0) {
        break;
      }
    }

    return { latestFoodTime: foodTime, latestDrinkTime: drinkTime };
  }, [orders]); // orders is the only dependency

  return { latestFoodTime, latestDrinkTime };
}
